<template>
    <div>
        <div id="range" class="component">
            <div class="middle" @click="toggle" title="What's new">
                <span class="pill pill-light pill-back" style="padding: 6px 8px 6px 8px">
                    <i class="material-icons">campaign</i>
                </span>
                <span class="changelog-indicator" v-if="hasUnseen" aria-label="New updates"></span>
            </div>
        </div>
        <div v-if="visible" class="changelog-panel" :class="{ 'changelog-panel--article': !!article }">
            <div class="panel-header">
                <ui-button v-if="article" icon="arrow_back" type="secondary" size="small" @click="closeArticle">Back</ui-button>
                <h1 v-else><span style="margin-right: 10px;">📢</span>What's new</h1>
            </div>

            <div class="panel-body">
                <div class="changelog-state" v-if="loading">
                    <ui-progress-circular :disable-transition="true" :size="32" />
                </div>
                <div class="changelog-state" v-else-if="error">
                    {{ error }}
                </div>

                <!-- Article -->
                <article v-else-if="article" class="changelog-article">
                    <h2 class="changelog-article__title">{{ article.title }}</h2>
                    <changelog-tags :tags="article.tags" />
                    <div class="changelog-article__content" v-html="article.html"></div>
                    <a v-if="article.url" :href="article.url" target="_blank" rel="noopener" class="changelog-link">
                        Read on the blog <i class="material-icons">open_in_new</i>
                    </a>
                </article>

                <!-- List -->
                <template v-else>
                    <div class="changelog-state" v-if="posts.length === 0">
                        No updates yet
                    </div>
                    <div
                        v-for="post in posts"
                        :key="post.id"
                        class="changelog-item"
                        role="button"
                        tabindex="0"
                        @click="openArticle(post)"
                        @keydown.enter="openArticle(post)"
                    >
                        <!-- Hero layout: full-width image above the post's text. -->
                        <div class="changelog-item__image" v-if="post.feature_image">
                            <img :src="post.feature_image" :alt="post.title" loading="lazy">
                        </div>
                        <div class="changelog-item__body">
                            <div class="changelog-item__title">{{ post.title }}</div>
                            <div class="changelog-item__excerpt" v-if="post.excerpt">{{ post.excerpt }}</div>
                            <changelog-tags :tags="post.tags" />
                        </div>
                    </div>
                </template>
            </div>

            <div class="panel-footer" v-if="blogUrl">
                <a :href="blogUrl" target="_blank" rel="noopener" class="changelog-link">
                    View all changes <i class="material-icons">open_in_new</i>
                </a>
            </div>
        </div>
    </div>
</template>

<script>
    import DealerKit from '../api'

    const ChangelogTags = {
        functional: true,
        props: { tags: { type: Array, default: () => [] } },
        render(h, { props }) {
            if (!props.tags.length) {
                return null
            }

            return h('div', { class: 'changelog-tags' }, props.tags.map(tag =>
                h('span', { class: 'changelog-tag', key: tag.slug }, tag.name)
            ))
        }
    }

    export default {
        components: { ChangelogTags },
        data() {
            return {
                visible: false,
                loading: false,
                loaded: false,
                error: null,
                posts: [],
                blogUrl: null,
                hasUnseen: false,
                article: null,
                loadPromise: null,
            }
        },
        mounted() {
            // Load up front so we know whether to show the new indicator.
            this.load()

            this.outsideClickHandler = (e) => {
                // Ignore targets we've just re-rendered away (e.g. list -> article).
                if (this.visible && document.contains(e.target) && !this.$el.contains(e.target)) {
                    this.close()
                }
            }

            document.addEventListener('click', this.outsideClickHandler)
        },
        beforeDestroy() {
            document.removeEventListener('click', this.outsideClickHandler)
        },
        methods: {
            toggle() {
                this.visible ? this.close() : this.open()
            },
            open() {
                this.visible = true

                const loading = this.loaded ? Promise.resolve() : this.load()

                loading.then(() => this.markSeen())
            },
            close() {
                this.visible = false
                this.article = null
                // Allow a failed load to be retried by reopening.
                this.error = null
            },
            load() {
                if (this.loadPromise) {
                    return this.loadPromise
                }

                this.loading = true
                this.error = null

                this.loadPromise = DealerKit.changelog.getChangelogPosts().then(({ data }) => {
                    this.posts = data.data
                    this.blogUrl = data.meta.blog_url
                    this.hasUnseen = data.meta.has_unseen
                    this.loaded = true
                }).catch(() => {
                    this.error = 'We couldn\'t load the latest updates, please try again later.'
                }).finally(() => {
                    this.loading = false
                    this.loadPromise = null
                })

                return this.loadPromise
            },
            markSeen() {
                if (!this.hasUnseen) {
                    return
                }

                this.hasUnseen = false

                // Best effort; worst case the indicator shows again next page load.
                DealerKit.changelog.markChangelogSeen().catch(() => {})
            },
            openArticle(post) {
                // Show what we already have straight away, then fill in the body.
                this.article = { ...post, html: null }
                this.loading = true
                this.error = null

                DealerKit.changelog.getChangelogPost(post.slug).then(({ data }) => {
                    if (this.article && this.article.slug === post.slug) {
                        this.article = data.data
                    }
                }).catch(() => {
                    this.error = 'We couldn\'t load this update, please try again later.'
                }).finally(() => {
                    this.loading = false
                })
            },
            closeArticle() {
                this.article = null
                this.loading = false
                this.error = null
            }
        }
    }
</script>

<style lang="scss" scoped>
    .changelog-indicator {
        position: absolute;
        top: -3px;
        right: -3px;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: #bc0101;
        pointer-events: none;
    }

    .changelog-panel {
        background: white;
        box-shadow: 0 0 5px 5px rgba(0, 0, 0, .1);
        border-radius: 5px;
        top: 70px;
        position: absolute;
        right: 15px;
        width: 360px;
        max-width: calc(100vw - 30px);
        max-height: calc(100vh - 90px);
        color: #42526e;
        display: flex;
        flex-direction: column;
        overflow: hidden;

        // Wider for reading an article; max-width still caps it on small screens.
        &.changelog-panel--article { width: 460px; }

        .panel-header {
            display: flex;
            align-items: center;
            gap: .5rem;
            padding: 0 1.25rem;
            height: 55px;
            flex-shrink: 0;
            border-bottom: 1px solid #f0f0f0;

            h1 { font-size: 20px; font-weight: 600; margin-bottom: 0; flex: 1; min-width: 0; }
        }

        .panel-body {
            overflow-y: auto;
            flex: 1;
        }

        .panel-footer {
            flex-shrink: 0;
            padding: .75rem 1.25rem;
            border-top: 1px solid #f0f0f0;
            text-align: center;
        }
    }

    .changelog-state {
        padding: 2rem 1.5rem 3rem;
        text-align: center;

        & > div { margin: auto; }
    }

    .changelog-item {
        display: flex;
        flex-direction: column;
        gap: .75rem;
        padding: 1rem 1.25rem;
        cursor: pointer;
        border-bottom: 1px solid #f5f5f5;

        &:last-child { border-bottom: 0; }
        &:hover, &:focus { background: #f7f8fa; outline: none; }

        &__image {
            width: 100%;
            aspect-ratio: 16 / 9;
            border-radius: 4px;
            overflow: hidden;
            background: #eef0f4;

            img { width: 100%; height: 100%; object-fit: cover; display: block; }
        }

        &__body { min-width: 0; }

        &__title { font-size: 1.05rem; font-weight: 600; color: #172b4d; line-height: 1.3; }

        &__excerpt {
            font-size: 16px;
            line-height: 1.4;
            margin-top: .25rem;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }
    }

    ::v-deep .changelog-tags {
        display: flex;
        flex-wrap: wrap;
        gap: .3rem;
        margin-top: .5rem;
    }

    ::v-deep .changelog-tag {
        text-transform: uppercase;
        display: inline-block;
        padding: 4px 8px;
        border: 1px solid #42526e;
        border-radius: 4px;
        font-size: 10px;
        line-height: 1;
        color: var(--color-text);
        white-space: nowrap;
    }

    .changelog-link {
        font-size: .85rem;
        font-weight: 500;
        display: inline-flex;
        align-items: center;
        gap: .25rem;

        i { font-size: 16px; }
    }

    .changelog-article {
        padding: 1.25rem 1.5rem 1.5rem;

        &__image {
            width: 100%;
            border-radius: 4px;
            margin-bottom: 1rem;
        }

        &__title { font-size: 1.35rem; font-weight: 600; color: #172b4d; line-height: 1.3; margin: 0; }

        &__content {
            margin: 1rem 0;
            font-size: 16px;
            overflow-wrap: break-word;

            ::v-deep {
                img, video, iframe { max-width: 100%; height: auto; border-radius: 4px; }
                iframe { aspect-ratio: 16 / 9; width: 100%; border: 0; }
                figure { margin: 1rem 0; }
                figcaption { font-size: .8rem; color: #7a869a; text-align: center; margin-top: .25rem; }
                h2, h3, h4 { color: #172b4d; font-weight: 600; margin: 1.25rem 0 .5rem; }
                h2 { font-size: 1.2rem; }
                h3 { font-size: 1.05rem; }
                ul, ol { padding-left: 1.25rem; }
                blockquote { border-left: 3px solid #dfe1e6; padding-left: 1rem; color: #5e6c84; }
                pre { background: #f4f5f7; padding: .75rem; border-radius: 4px; overflow-x: auto; }
            }
        }
    }
</style>
